import { useFlag, useVariant } from '@unleash/proxy-client-react';
import { useEffect } from 'react';
import { useAppDispatch } from '../../app/hooks';
import { update } from '../../redux/features/config/config.slice';
import { setAuthRefactorFeatureFlag } from '../../utils/session-storage';
import { setAgentInactivityFeatureFlag } from '../../utils/session-storage';
import {
  AGENT_CART_FLAG_NAME,
  DO_NOT_UPSELL_IN_EXPRESS_MODE_FLAG_NAME,
  SESSION_END_ORDER_ACTION_CHANGES_FLAG_NAME,
  SUPPRESS_UPSELL_FLAG_NAME,
  TIMER_RESET_ON_CART_UPDATE_FLAG_NAME,
  UNRESOLVED_ITEMS_FLAG_NAME,
  HITL_3_0_FLAG_NAME,
  HITL_EXPRESS_TIMER_FLAG_NAME,
} from '../../redux/features/config/config.flags';

const useUnleashHooks = () => {
  const audioBackend = useFlag('audio_backend');
  const autoCombo = useFlag('auto_combo');
  const bmAutoCombo = useFlag('bm_auto_combo');
  const authRefactor = useFlag('auth_refactor');
  const agentInactivityRefactor = useFlag('agent_inactivity_refactor');
  const finalOrderValidation = useFlag('final_order_validation');
  const posCart = useFlag('pos_cart');
  const loyaltySupport = useFlag('loyalty_support');
  const itemConversion = useFlag('item_conversion');
  const notifyHitlBuild = useFlag('notify_hitl_build');
  const useMenuModuleForOrderSubmission = useFlag(
    'use_menu_module_for_order_submission'
  );
  const sessionEndOrderActionChanges = useFlag(
    SESSION_END_ORDER_ACTION_CHANGES_FLAG_NAME
  );
  const timerResetOnCartUpdate = useFlag(TIMER_RESET_ON_CART_UPDATE_FLAG_NAME);
  const suppressUpsell = useFlag(SUPPRESS_UPSELL_FLAG_NAME);
  const doNotUpsellInExpressMode = useFlag(
    DO_NOT_UPSELL_IN_EXPRESS_MODE_FLAG_NAME
  );
  const unresolvedItems = useFlag(UNRESOLVED_ITEMS_FLAG_NAME);
  const hitl3 = useFlag(HITL_3_0_FLAG_NAME);
  const agentCart = useFlag(AGENT_CART_FLAG_NAME);
  const expressTimer = useFlag(HITL_EXPRESS_TIMER_FLAG_NAME);
  // The `hitl_express_timer` flag carries a `max_seconds` variant whose payload
  // value is the order-time threshold (seconds). 0 / missing => no threshold.
  const expressTimerVariant = useVariant(HITL_EXPRESS_TIMER_FLAG_NAME);
  const expressTimerMaxSeconds =
    parseInt(expressTimerVariant?.payload?.value ?? '', 10) || 0;

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_AUDIO_BACKEND: audioBackend }));
  }, [audioBackend]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_AUTO_COMBO: autoCombo }));
  }, [autoCombo]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_BM_AUTO_COMBO: bmAutoCombo }));
  }, [bmAutoCombo]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_LOYALTY_SUPPORT: loyaltySupport }));
  }, [loyaltySupport]);

  useEffect(() => {
    setAgentInactivityFeatureFlag((!!agentInactivityRefactor).toString());
  }, [agentInactivityRefactor]);

  useEffect(() => {
    setAuthRefactorFeatureFlag((!!authRefactor).toString());
  }, [authRefactor]);

  useEffect(() => {
    dispatch(
      update({ FEATURE_FLAG_FINAL_ORDER_VALIDATION: finalOrderValidation })
    );
  }, [finalOrderValidation]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_POS_CART: posCart }));
  }, [posCart]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_ITEM_CONVERSION: itemConversion }));
  }, [itemConversion, dispatch]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_NOTIFY_HITL_BUILD: notifyHitlBuild }));
  }, [notifyHitlBuild]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_USE_MENU_MODULE_FOR_ORDER_SUBMISSION:
          useMenuModuleForOrderSubmission,
      })
    );
  }, [useMenuModuleForOrderSubmission]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_SESSION_END_ORDER_ACTION_CHANGES:
          sessionEndOrderActionChanges,
      })
    );
  }, [sessionEndOrderActionChanges]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_TIMER_RESET_ON_CART_UPDATE: timerResetOnCartUpdate,
      })
    );
  }, [timerResetOnCartUpdate]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_SUPPRESS_UPSELL: suppressUpsell }));
  }, [suppressUpsell]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_DO_NOT_UPSELL_IN_EXPRESS_MODE: doNotUpsellInExpressMode,
      })
    );
  }, [doNotUpsellInExpressMode]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_UNRESOLVED_ITEMS: unresolvedItems }));
  }, [unresolvedItems]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_HITL_3_0: hitl3 }));
  }, [hitl3]);

  useEffect(() => {
    dispatch(update({ FEATURE_FLAG_AGENT_CART: agentCart }));
  }, [agentCart]);

  useEffect(() => {
    dispatch(
      update({
        FEATURE_FLAG_HITL_EXPRESS_TIMER: expressTimer,
        HITL_EXPRESS_TIMER_MAX_SECONDS: expressTimerMaxSeconds,
      })
    );
  }, [expressTimer, expressTimerMaxSeconds]);
};

export default useUnleashHooks;
